/**
 * OfflineDatabase - Base de données IndexedDB pour le mode hors-ligne
 *
 * Utilise Dexie.js pour une API simple et typée sur IndexedDB.
 * Stocke les produits, catégories, vendeurs et ventes en attente.
 */
import Dexie, { Table } from 'dexie';

// ============================================================================
// TYPES POUR LES TABLES OFFLINE
// ============================================================================

export interface OfflineProduct {
  id: string;
  tenant_id: string;
  code: string | null;
  barcode: string | null;
  name: string;
  description: string | null;
  image_url: string | null;
  category_id: string | null;
  category_name: string | null;

  // Prix
  price_cents: number;
  currency: string;

  // Stock (snapshot au moment de la sync)
  stock_quantity: number;
  stock_display: number;
  stock_backroom: number;
  is_weighted: boolean;
  unit: string | null;

  // TVA
  tax_class: string; // 'STANDARD_21' | 'REDUIT_12' | 'ALIM_6' | 'ZERO_0'

  // Consignes
  is_deposit: boolean;
  deposit_product_id: string | null;
  deposit_qty_per_unit: number | null;

  // Remise anti-gaspi par lot (sinon la fenêtre de choix ne s'ouvre pas hors ligne)
  lot_promos?: Array<{ lot_id: string; lot_number: string; expiry_date: string | null; percent: number; price_cents: number }>;
  lot_normal_expiry_date?: string | null;

  // Optimistic locking
  version: number;

  // Sync metadata
  _lastSync: number;
}

export interface OfflineCategory {
  id: string;
  tenant_id: string;
  name: string;
  parent_id: string | null;
  sort_order: number;
  image_url: string | null;
  _lastSync: number;
}

export interface OfflineVendor {
  id: string;
  tenant_id: string;
  name: string;
  pin_hash: string;
  role: 'ADMIN' | 'MANAGER' | 'CASHIER' | 'CASHIER_PLUS';
  workstations: string[];
  active: boolean;
  _lastSync: number;
}

export interface PendingSaleLine {
  product_id?: string;
  product_name: string;
  quantity: number;
  unit_price_cents: number;
  total_price_cents: number;
  tax_rate: number;
  is_deposit: boolean;
  is_return: boolean;
  /** Ligne montant libre. Déclaré ET transmis : ces deux champs manquaient à
   *  l'énumération de `syncSale`, si bien que sur ~49 000 lignes de vente en
   *  production, 3 seulement portaient `is_custom` et aucune note justificative
   *  n'atteignait la base — on ne pouvait plus retrouver pourquoi un montant
   *  libre avait été encaissé. */
  is_custom?: boolean;
  notes?: string;
  /** Vente d'un bon cadeau : dette, ni CA ni TVA. La caisse refuse de vendre un
   *  bon hors ligne (le code vient du serveur), mais le drapeau voyage quand même :
   *  un champ qu'on oublie de transmettre est un champ qu'on perd en silence. */
  is_gift_voucher?: boolean;
  /** Remise anti-gaspi par lot (lot choisi au scan). */
  lot_id?: string;
  lot_promo_percent?: number;
  original_price_cents?: number;
}

export interface PendingSale {
  id: string; // UUID local
  localSaleNumber: string; // "OFFLINE-{timestamp}-{random}"
  serverSaleNumber?: string; // Attribué après sync

  tenant_id: string;
  vendor_id: string | null;
  vendor_name: string | null;
  customer_id: string | null;

  payment_method: string;
  amount_paid_cents: number | null;
  payment_metadata?: Record<string, number> | null;
  total_cents: number;
  tax_total_cents: number;
  change_cents: number | null;

  lines: PendingSaleLine[];

  // Précommande associée (si applicable)
  preorder_id: string | null;

  // Commande e-commerce associée (retrait au comptoir)
  ecom_order_id?: string | null;
  sales_channel?: string;

  // Facture Enfact demandée par le client
  invoice_requested?: boolean;

  // Forçage : enregistrer la vente malgré un stock insuffisant (oversell hors-ligne assumé)
  force?: boolean;

  // Sync status
  // 'abandoned' : vente encaissée puis abandonnée par l'opérateur (conflit insoluble).
  // Elle n'est JAMAIS effacée : elle est envoyée au serveur (POST /pos/sales/abandoned)
  // avec son contenu et son motif, puis reste ici marquée. Audit ISCA 30/08/2026.
  status: 'pending' | 'syncing' | 'synced' | 'conflict' | 'error' | 'abandoned';
  abandonReason?: string;
  abandoned_at?: number;
  abandon_synced?: boolean; // l'abandon a été enregistré côté serveur
  syncing_at?: number; // ms epoch — instant du passage en 'syncing' (détection des requêtes figées)
  errorMessage?: string;
  conflictDetails?: {
    product_id: string;
    product_name: string;
    requested: number;
    available: number;
    /** Produit de notre production (récolte vendue avant d'être encodée). */
    own_production?: boolean;
    /** ms epoch — moment où le serveur a répondu ces chiffres. */
    checked_at?: number;
  };
  backroomDetails?: {
    product_id: string;
    product_name: string;
    backroom_quantity: number;
    lot_id: string | null;
    requested: number;
  };
  retryCount: number;

  created_at: number; // Timestamp
  synced_at?: number;
}

export interface LocalStockMovement {
  id: string;
  product_id: string;
  quantity_delta: number; // Négatif pour les ventes
  reason: 'sale' | 'adjustment' | 'sync';
  reference_id: string; // ID de la vente locale
  created_at: number;
  synced: boolean;
}

export interface SyncLog {
  id?: number;
  timestamp: number;
  type: 'catalog' | 'sales' | 'stock' | 'full';
  status: 'started' | 'success' | 'error' | 'partial';
  details: string;
  itemCount?: number;
  duration?: number;
}

// ============================================================================
// CLASSE PRINCIPALE DEXIE
// ============================================================================

export class OfflineDatabase extends Dexie {
  products!: Table<OfflineProduct, string>;
  categories!: Table<OfflineCategory, string>;
  vendors!: Table<OfflineVendor, string>;
  pendingSales!: Table<PendingSale, string>;
  stockMovements!: Table<LocalStockMovement, string>;
  syncLogs!: Table<SyncLog, number>;

  constructor() {
    super('FoliesPOS');

    // Schéma de la base de données
    // Les clés primaires sont avant la virgule, les index après
    this.version(1).stores({
      // Produits : recherche par barcode, code, category, tenant
      products: 'id, tenant_id, barcode, code, category_id, name, _lastSync, [tenant_id+barcode], [tenant_id+code]',

      // Catégories : tri par sort_order
      categories: 'id, tenant_id, parent_id, sort_order',

      // Vendeurs : recherche par tenant et statut actif
      vendors: 'id, tenant_id, [tenant_id+active]',

      // Ventes en attente : recherche par status et tenant
      pendingSales: 'id, tenant_id, status, created_at, [tenant_id+status]',

      // Mouvements de stock locaux
      stockMovements: 'id, product_id, synced, created_at, reference_id',

      // Logs de synchronisation
      syncLogs: '++id, timestamp, type, status',
    });
  }

  // ============================================================================
  // MÉTHODES UTILITAIRES
  // ============================================================================

  /**
   * Vider toutes les données d'un tenant
   */
  async clearTenantData(tenantId: string): Promise<void> {
    await this.transaction('rw', [this.products, this.categories, this.vendors, this.pendingSales, this.stockMovements], async () => {
      await this.products.where('tenant_id').equals(tenantId).delete();
      await this.categories.where('tenant_id').equals(tenantId).delete();
      await this.vendors.where('tenant_id').equals(tenantId).delete();
      // ⚠️ NE PURGER QUE les ventes SYNCHRONISÉES. Les ventes non synchronisées
      // (pending/syncing/conflict/error) sont des ventes ENCAISSÉES pas encore
      // remontées au serveur — les supprimer au logout (surtout l'auto-logout par
      // inactivité) effacerait une journée de marché hors-ligne. Elles survivent et
      // se synchroniseront au retour réseau.
      await this.pendingSales.where('[tenant_id+status]').equals([tenantId, 'synced']).delete();
      // stockMovements n'a pas de tenant_id direct, on les garde
    });
  }

  /**
   * Obtenir les statistiques de la base
   */
  async getStats(tenantId: string): Promise<{
    products: number;
    categories: number;
    vendors: number;
    pendingSales: number;
    syncedSales: number;
    lastSync: Date | null;
  }> {
    const [products, categories, vendors, pendingSales, syncedSales, lastSyncLog] = await Promise.all([
      this.products.where('tenant_id').equals(tenantId).count(),
      this.categories.where('tenant_id').equals(tenantId).count(),
      this.vendors.where('tenant_id').equals(tenantId).count(),
      this.pendingSales.where('[tenant_id+status]').equals([tenantId, 'pending']).count(),
      this.pendingSales.where('[tenant_id+status]').equals([tenantId, 'synced']).count(),
      this.syncLogs.where('type').equals('catalog').and(log => log.status === 'success').last(),
    ]);

    return {
      products,
      categories,
      vendors,
      pendingSales,
      syncedSales,
      lastSync: lastSyncLog ? new Date(lastSyncLog.timestamp) : null,
    };
  }

  /**
   * Nettoyer les ventes synchronisées de plus de 7 jours
   */
  async cleanupOldSyncedSales(): Promise<number> {
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

    const toDelete = await this.pendingSales
      .where('status')
      .equals('synced')
      .and(sale => (sale.synced_at ?? 0) < sevenDaysAgo)
      .primaryKeys();

    await this.pendingSales.bulkDelete(toDelete);
    return toDelete.length;
  }

  /**
   * Nettoyer les produits obsolètes (non mis à jour depuis 48h)
   */
  async cleanupStaleProducts(tenantId: string): Promise<number> {
    const twoDaysAgo = Date.now() - 48 * 60 * 60 * 1000;

    const toDelete = await this.products
      .where('tenant_id')
      .equals(tenantId)
      .and(p => p._lastSync < twoDaysAgo)
      .primaryKeys();

    await this.products.bulkDelete(toDelete);
    return toDelete.length;
  }
}

// ============================================================================
// INSTANCE SINGLETON
// ============================================================================

export const offlineDb = new OfflineDatabase();

// ============================================================================
// HELPERS POUR REQUÊTES FRÉQUENTES
// ============================================================================

/**
 * Rechercher un produit par code-barres
 */
export async function findProductByBarcode(
  tenantId: string,
  barcode: string
): Promise<OfflineProduct | undefined> {
  return offlineDb.products
    .where('[tenant_id+barcode]')
    .equals([tenantId, barcode])
    .first();
}

/**
 * Rechercher un produit par code
 */
export async function findProductByCode(
  tenantId: string,
  code: string
): Promise<OfflineProduct | undefined> {
  return offlineDb.products
    .where('[tenant_id+code]')
    .equals([tenantId, code])
    .first();
}

/**
 * Rechercher des produits par nom (fuzzy)
 */
export async function searchProductsByName(
  tenantId: string,
  query: string,
  limit: number = 20
): Promise<OfflineProduct[]> {
  const normalizedQuery = query.toLowerCase().trim();

  return offlineDb.products
    .where('tenant_id')
    .equals(tenantId)
    .filter(p => p.name.toLowerCase().includes(normalizedQuery))
    .limit(limit)
    .toArray();
}

/**
 * Obtenir les produits d'une catégorie
 */
export async function getProductsByCategory(
  tenantId: string,
  categoryId: string
): Promise<OfflineProduct[]> {
  return offlineDb.products
    .where('tenant_id')
    .equals(tenantId)
    .and(p => p.category_id === categoryId)
    .toArray();
}

/**
 * Obtenir le nombre de ventes en attente
 */
export async function getPendingSalesCount(tenantId: string): Promise<number> {
  return offlineDb.pendingSales
    .where('[tenant_id+status]')
    .equals([tenantId, 'pending'])
    .count();
}

/**
 * Obtenir les ventes en erreur
 */
export async function getErrorSales(tenantId: string): Promise<PendingSale[]> {
  return offlineDb.pendingSales
    .where('tenant_id')
    .equals(tenantId)
    .and(sale => sale.status === 'error' || sale.status === 'conflict')
    .toArray();
}
